import React from 'react';
import styled from 'styled-components';
import { utils } from '@makeship/core';
import StyledLink from '@makeship/core/lib/components/Link';
import { Caption, P2, Strong } from '../../../Typography';

import { getProductFromContext } from '../../../../context/product';
import { getProductIDFromShopifyGID, getProductType, parseMetafields } from '../../../../utils/product';
import { ProductType } from '../../../../types/common';
import { AccordionList } from '../../../Accordion';
import { getPDPTrackingLocation, handleSupportLinkTracking } from '../../../../utils/analytics';
import { isHolidayHideawayProduct } from '../../../../utils/bfcm';
import MakeGoodDisclaimer from '../../../Disclaimer/MakegoodDisclaimer';
import Pride2025Disclaimer from '../../../Disclaimer/Pride2025Disclaimer';

const SpecsWrapper = styled.div``;

const SingleSpecWrapper = styled.div`
  margin-bottom: 24px;
`;

const FaintCaption = styled(Caption)`
  color: ${({ theme }) => utils.hexToRGBA(theme.colors.neutral7, theme.alpha.dark)};
`;

const SpecsList = styled.ul`
  list-style-type: disc;
`;

const SpecsListItem = styled.li`
  list-style-type: disc;
  margin-left: 30px;
`;

const ProductSpecsText = styled(P2)``;

const AccordionBodyHeading = styled(Caption)`
  color: ${({ theme }) => utils.hexToRGBA(theme.colors.neutral7, theme.alpha.dark)};
`;

const SpecsToggle: React.FC = () => {
  const product = getProductFromContext();

  const metafields = parseMetafields(product.metafields);
  const productType = getProductType(product.productType);
  const isMakeGoodProduct =
    getProductIDFromShopifyGID(product.id) === '7519085691019' ||
    getProductIDFromShopifyGID(product.id) === '8365980188828';

  const isPrideProduct =
    getProductIDFromShopifyGID(product.id) === '7825844142219' ||
    getProductIDFromShopifyGID(product.id) === '8386863988892';

  // GT June 2025
  const isGorillaTagProduct =
    // staging
    getProductIDFromShopifyGID(product.id) === '7832870322315' ||
    // production
    getProductIDFromShopifyGID(product.id) === '8442920566940';

  type ProductDimensions = {
    name: string;
    unit: string;
    default?: string;
    key: string;
  };

  type ProductCareType = {
    careInstructions?: string;
    material: string[];
    dimensions?: ProductDimensions[];
    disclaimer?: string;
    weight?: ProductDimensions[];
  };

  type ProductCare = {
    [type: string]: ProductCareType;
  };

  const getProductWarning = (productType: ProductType): string => {
    switch (productType) {
      case ProductType.Plushie:
      case ProductType.Longboi:
      case ProductType.Doughboi:
      case ProductType.VinylFigure:
      case ProductType.KeychainPlushie:
        return 'WARNING: CHOKING HAZARD. Small parts. Not for children under 3 years of age. Adult collectible. Not a toy. Recommended for ages 15 and up.';
      case ProductType.EnamelPin1Pack:
      case ProductType.EnamelPin2Pack:
      case ProductType.EnamelPin3Pack:
        return 'WARNING: CHOKING HAZARD. Small parts and sharp edges. Not for children under 3 years of age. Adult collectible. Not a toy. Recommended for ages 15 and up.';
      default:
        return '';
    }
  };

  // TO DO: Refactor when UI discrepancies are resolved.
  // We currently have 2 different ways of handling rendering product details due to different mockups for different product types.
  const getProductCare = (metafields: Metafields): ProductCare => ({
    [ProductType.Plushie]: {
      careInstructions: 'Handwash warm or cool only. Do not machine wash or dry clean.',
      material:
        metafields.promotion === 'gitd'
          ? ['Polypropylene Cotton', 'Plush Fabric (Ultra Soft Fleece)', 'Luminescent Plush Fabric']
          : ['Polypropylene Cotton', 'Plush Fabric (Ultra Soft Fleece)'],
      dimensions: [
        {
          name: 'Height',
          unit: 'cm',
          default: '16',
          key: 'height',
        },
        {
          name: 'Width',
          unit: 'cm',
          key: 'width',
        },
        {
          name: 'Depth',
          unit: 'cm',
          key: 'depth',
        },
      ],
      disclaimer: 'Some heights may vary due to hair, or additional accessories.',
    },
    [ProductType.Longboi]: {
      careInstructions: 'Handwash warm or cool only. Do not machine wash or dry clean.',
      material: ['Polypropylene Cotton', 'Plush Fabric (Ultra Soft Fleece)'],
      dimensions: [
        {
          name: 'Height',
          unit: 'cm',
          default: '11.5',
          key: 'height',
        },
        {
          name: 'Length',
          unit: 'cm',
          default: '50',
          key: 'length',
        },
      ],
      disclaimer: 'Some heights/lengths may vary due to hair, or additional accessories.',
    },
    [ProductType.Sketchbook]: {
      careInstructions: '',
      material: [
        'Sketchbook (60 Pages): 250g Cardboard, 216g Sketch paper',
        'Cover: 350g Cardboard',
        'Artbook (5 pages): 250g Coated Paper, 200g Sketch paper',
        'Label Sleeve: 250g Cardboard',
      ],
      dimensions: [
        {
          name: 'Height',
          unit: 'cm',
          default: '15',
          key: 'height',
        },
        {
          name: 'Length',
          unit: 'cm',
          default: '21',
          key: 'length',
        },
        {
          name: 'Width',
          unit: 'cm',
          default: '2',
          key: 'width',
        },
      ],
    },
    [ProductType.Charm]: {
      careInstructions:
        'Remove before exercising or showering, store in pouch, and clean with jewelry polishing cloth to preserve gold plating.',
      material: [
        `18K ${metafields.color === 'gold' ? 'Gold' : 'White Gold'} Plated`,
        'Enamel Graphic',
        'Sterling Silver Base',
      ],
      dimensions: [
        {
          name: 'Height',
          unit: 'cm',
          default: '2',
          key: 'height',
        },
        {
          name: 'Length',
          unit: 'cm',
          default: '2',
          key: 'length',
        },
        {
          name: 'Depth',
          unit: 'mm',
          default: '1',
          key: 'depth',
        },
      ],
    },
    [ProductType.Backpack]: {
      careInstructions: 'Hand wash warm or cool only. Do not machine wash or dry clean.',
      material: [
        'Exterior: Canvas, PU Leather Detailing (Top Handle, Base, Zipper Pull)',
        'Interior: Cotton/Polyester Blend',
        'Adjustable Straps: Canvas and Cotton',
        'Zipper and Buckles: Metal',
        'Graphic: Screen Printing ',
      ],
      dimensions: [
        {
          name: 'Height',
          unit: 'cm',
          default: '43',
          key: 'height',
        },
        {
          name: 'Width',
          unit: 'cm',
          default: '29',
          key: 'length',
        },
      ],
    },
    [ProductType.KeychainPlushie]: {
      careInstructions: 'Handwash warm or cool only. Do not machine wash or dry clean.',
      material: [
        `Interior (main body): Polypropylene Cotton`,
        'Exterior: Plush Fabric (Ultra Soft Fleece)',
        'Band: Silicone',
      ],
      dimensions: [
        {
          name: 'Height',
          unit: 'cm',
          default: '8',
          key: 'height',
        },
        {
          name: 'Width',
          unit: 'cm',
          default: '5',
          key: 'width',
        },
        {
          name: 'Depth',
          unit: 'cm',
          default: '5',
          key: 'depth',
        },
      ],
      disclaimer: 'Some heights may vary due to hair, or additional accessories.',
    },
    [ProductType.JumboPlush]: {
      careInstructions: 'Handwash warm or cool only. Do not machine wash or dry clean.',
      material:
        metafields.promotion === 'gitd'
          ? ['Polypropylene Cotton', 'Plush Fabric (Ultra Soft Fleece)', 'Luminescent Plush Fabric']
          : ['Polypropylene Cotton', 'Plush Fabric (Ultra Soft Fleece)'],
      dimensions: [
        {
          name: 'Height',
          unit: 'cm',
          default: '24',
          key: 'height',
        },
        {
          name: 'Width',
          unit: 'cm',
          default: '24',
          key: 'width',
        },
      ],
      disclaimer: 'Some heights may vary due to hair, or additional accessories.',
    },
    [ProductType.SquishyPlush]: {
      careInstructions: 'Handwash warm or cool only. Do not machine wash or dry clean.',
      material: [
        'Interior (main body): Memory Foam',
        'Interior (external structures): PP Cotton Stuffing ',
        'Exterior: Stretchy Plush Fabric and Embroidery',
      ],
      dimensions: [
        {
          name: 'Height',
          unit: 'cm',
          default: '19',
          key: 'height',
        },
        {
          name: 'Width',
          unit: 'cm',
          default: '19.5',
          key: 'width',
        },
        {
          name: 'Depth',
          unit: 'cm',
          default: '16',
          key: 'depth',
        },
      ],
      disclaimer: 'Some heights may vary due to hair, or additional accessories.',
    },
    [ProductType.VinylFigure]: {
      careInstructions:
        'To keep your figure in optimal condition limit exposure to these five elements: dust, oil, sunlight, heat, moisture. Long-term exposure to these elements can lead to eventual fading or weakening of Vinyl material.  Figures can be cleaned using either a microfiber or cotton cloth that is dry or lightly damp. If using water be sure to pat (not rub) the figure dry after cleaning. If additional care is needed, please use a Q-tip or cloth (microfiber or cotton) along with household dish soap and warm water to gently spot clean any affected areas.',
      material: ['Coloured PVC', 'Oil Paint and Water-Based Paint', 'Varnish'],
      dimensions: [
        {
          name: 'Height',
          unit: 'cm',
          default: '10',
          key: 'height',
        },
        {
          name: 'Width',
          unit: 'cm',
          default: '6',
          key: 'width',
        },
        {
          name: 'Depth',
          unit: 'cm',
          default: '5',
          key: 'depth',
        },
      ],
      disclaimer: 'Figure dimensions will vary depending on specific character and pose.',
    },
    [ProductType.FannyPack]: {
      careInstructions: 'Handwash warm or cool only. Do not machine wash or dry clean.',
      material: ['Exterior: Nylon Polyester', 'Interior: Cotton', 'Zipper: Metal'],
      dimensions: [
        {
          name: 'Height',
          unit: 'cm',
          default: '15',
          key: 'height',
        },
        {
          name: 'Width',
          unit: 'cm',
          default: '23',
          key: 'width',
        },
        {
          name: 'Depth',
          unit: 'cm',
          default: '7.5',
          key: 'depth',
        },
      ],
    },
    [ProductType.EnamelPin2Pack]: {
      material: ['Plated Zinc Alloy'],
      dimensions: [
        {
          name: 'Length',
          unit: 'cm',
          default: '3.8',
          key: 'length',
        },
        {
          name: 'Width',
          unit: 'cm',
          default: '3.8',
          key: 'width',
        },
        {
          name: 'Depth',
          unit: 'cm',
          default: '0.2',
          key: 'depth',
        },
      ],
      disclaimer: 'Some lengths and/or widths may vary based on specific character pose.',
      weight: [
        {
          name: 'Weight',
          unit: 'grams',
          default: '11.4',
          key: 'weight',
        },
      ],
    },
    [ProductType.EnamelPin3Pack]: {
      material: ['Plated Zinc Alloy'],
      dimensions: [
        {
          name: 'Length',
          unit: 'cm',
          default: '3.8',
          key: 'length',
        },
        {
          name: 'Width',
          unit: 'cm',
          default: '3.8',
          key: 'width',
        },
        {
          name: 'Depth',
          unit: 'cm',
          default: '0.2',
          key: 'depth',
        },
      ],
      disclaimer: 'Some lengths and/or widths may vary based on specific character pose.',
      weight: [
        {
          name: 'Weight',
          unit: 'grams',
          default: '11.4',
          key: 'weight',
        },
      ],
    },
    [ProductType.Doughboi]: {
      careInstructions: 'Handwash warm or cool only. Do not machine wash or dry clean.',
      material: ['Spandex Plush Fabric', '3D Cotton Down Filling'],
      dimensions: [
        {
          name: 'Height',
          unit: 'cm',
          default: '16.0',
          key: 'height',
        },
      ],
      disclaimer: 'Note: Some heights may vary due to additional details.',
    },
  });

  const renderMaterialsSection = (materials: (string | JSX.Element)[]) => (
    <>
      <AccordionBodyHeading>Materials</AccordionBodyHeading>
      <AccordionList>
        {materials.map((material) => (
          <ProductSpecsText>{material}</ProductSpecsText>
        ))}
      </AccordionList>
    </>
  );

  const renderCareInstructions = (instructions: (string | JSX.Element)[]) => (
    <>
      <AccordionBodyHeading>Care Instructions</AccordionBodyHeading>
      <AccordionList>
        {instructions.map((instruction) => (
          <ProductSpecsText>{instruction}</ProductSpecsText>
        ))}
      </AccordionList>
    </>
  );

  const renderFit = (fitDescription: string) => (
    <>
      <AccordionBodyHeading>Fit</AccordionBodyHeading>
      <AccordionList>{fitDescription}</AccordionList>
    </>
  );

  const renderDisclaimer = () => <ProductSpecsText>{metafields.disclaimer}</ProductSpecsText>;

  const renderSpecsList = (specs: { name: string; key: string; unit: string; default?: string }[]) => (
    <SpecsList>
      {specs.map((spec) => {
        const value = metafields[spec.key as keyof typeof metafields] || spec.default;

        if (!value) return null; // skip rendering if value is falsy (both metafield and default are missing)

        return (
          <SpecsListItem key={spec.name}>
            <P2>
              {spec.name}: {value}
              {spec.unit}
            </P2>
          </SpecsListItem>
        );
      })}
    </SpecsList>
  );

  const renderContactMakeshipSupport = () => (
    <ProductSpecsText>
      Please contact{' '}
      <StyledLink.Primary
        href="https://support.makeship.com/hc/en-us/requests/new"
        onClick={() => handleSupportLinkTracking('open_support_ticket', getPDPTrackingLocation(product), product)}
      >
        Makeship Customer Support
      </StyledLink.Primary>{' '}
      with any questions or if you need assistance.
    </ProductSpecsText>
  );

  if (productType === ProductType.Hoodie) {
    return (
      <>
        {renderMaterialsSection([
          '55% cotton, 45% polyester, Preshrunk',
          '(Exclusive of decorative details. Fabric color may vary.)',
          <Strong>Our premium poly-cotton blend offers a warm, low maintenance and more durable unisex Hoodie.</Strong>,
        ])}
        <br />
        {renderFit('Unisex, relaxed fit')}
        <br />
        {!!metafields.disclaimer && (
          <>
            {renderDisclaimer()}
            <br />
          </>
        )}
        {renderCareInstructions(['Machine washable. Please follow care instructions on Hoodie label.'])}
        <br />
        {renderContactMakeshipSupport()}
      </>
    );
  }

  if (productType === ProductType.Crewneck) {
    return (
      <>
        {renderMaterialsSection(['100% cotton yarn (Exclusive of decorative details.)'])}
        <br />
        {renderFit('Unisex')}
        <br />
        {!!metafields.disclaimer && (
          <>
            {renderDisclaimer()}
            <br />
          </>
        )}
        {renderCareInstructions([
          'Machine wash cold inside out. Do not bleach. Tumble dry low. Do not iron on print. Keep away from fire. Please follow all care instructions on the garment label.',
        ])}
        <br />
        {renderContactMakeshipSupport()}
      </>
    );
  }

  if (productType === ProductType.TShirt) {
    return (
      <>
        {renderMaterialsSection([
          '100% cotton, Preshrunk (Fabric color may vary)',
          <Strong>
            Made from 100% cotton, this unisex t-shirt has a relaxed fit with a classic crew neckline and straight hem.
            The fabric is pre-shrunk to help it keep its shape and fit, wash after wash.
          </Strong>,
        ])}
        <br />
        {renderFit('Unisex, relaxed fit')}
        <br />
        {!!metafields.disclaimer && (
          <>
            {renderDisclaimer()}
            <br />
          </>
        )}
        {renderCareInstructions([
          'Wash inside out. Machine was cold. Tumble dry low. Do not iron appliqué. Do not bleach. Do not dry clean. Wash with like colors. Please follow all care instructions on the garment label.',
        ])}
        <br />
        {renderContactMakeshipSupport()}
      </>
    );
  }

  if (productType === ProductType.Sweatpants) {
    return (
      <>
        {renderMaterialsSection([
          '50% cotton, 50% polyester, 300GSM, Preshrunk (Fabric color may vary)',
          <Strong>
            These unisex sweatpants feature a relaxed fit with cuffed hems, side seam pockets, and an elastic waistband
            with a drawstring for a customizable fit. Made from a 55/45 cotton-poly fleece, they&apos;re pre-shrunk and
            brushed on the inside for a soft, lived-in feel from day one.
          </Strong>,
        ])}
        <br />
        {renderFit('Unisex, relaxed fit')}
        <br />
        {!!metafields.disclaimer && (
          <>
            {renderDisclaimer()}
            <br />
          </>
        )}
        {renderCareInstructions([
          'Wash inside out. Machine wash cold. Tumble dry low. Do not iron appliqué. Do not bleach. Do not dry clean. Wash with like colors. Please follow all care instructions on the garment label.',
        ])}
        <br />
        {renderContactMakeshipSupport()}
      </>
    );
  }

  if (productType === ProductType.CottonSocks) {
    return (
      <>
        {renderMaterialsSection([
          '80% cotton, 20% polyester + spandex (Fabric color may vary)',
          <Strong>
            These unisex crew socks are designed for all-day comfort, featuring a ribbed cuff, soft cushioning, and
            seamless construction for a smooth, secure fit. Made from a premium cotton blend with added stretch,
            they&apos;re built to last—whether you&apos;re lounging, commuting, or on the go.
          </Strong>,
        ])}
        <br />
        {renderFit('Unisex, secure fit')}
        <br />
        {!!metafields.disclaimer && (
          <>
            {renderDisclaimer()}
            <br />
          </>
        )}
        {renderCareInstructions([
          'Machine wash cold. Tumble dry low. Do not bleach. Do not iron. Do not dry clean. Wash with like colors. Please follow all care instructions on the garment label.',
        ])}
        <br />
        {renderContactMakeshipSupport()}
      </>
    );
  }

  if (productType === ProductType.TruckerHat) {
    return (
      <>
        <SingleSpecWrapper>
          <FaintCaption>Dimensions</FaintCaption>
          <P2>Circumference: 21.2&quot; - 21.6&quot;</P2>
        </SingleSpecWrapper>
        {renderMaterialsSection([
          'Polyester + sponge (Fabric color may vary)',
          <Strong>
            This unisex trucker hat has a structured design with a slightly curved brim, mesh back for ventilation, and
            an adjustable snapback for a comfortable, secure fit.
          </Strong>,
        ])}
        <br />
        {renderFit('Unisex, structured + adjustable')}
        <br />
        {renderCareInstructions([
          'Hand wash or spot clean as needed. Do not bleach. Do not iron. Hang to dry. Do not twist or wring. Please follow all care instructions on the garment label.',
        ])}
        <br />
        {renderContactMakeshipSupport()}
      </>
    );
  }

  if (productType === ProductType.BallCap) {
    return (
      <>
        <SingleSpecWrapper>
          <FaintCaption>Dimensions</FaintCaption>
          <P2>Circumference: 21.2&quot; - 21.6&quot;</P2>
        </SingleSpecWrapper>
        {renderMaterialsSection([
          '100% cotton (Fabric color may vary)',
          <Strong>
            This unisex ball cap features a curved brim and adjustable back strap for a perfect fit. Made from 100%
            cotton for a breathable, lightweight feel.
          </Strong>,
        ])}
        <br />
        {renderFit('Unisex, lightweight + adjustable')}
        <br />
        {renderCareInstructions([
          'Hand wash or spot clean as needed. Do not bleach. Do not iron. Hang to dry. Do not twist or wring. Please follow all care instructions on the garment label.',
        ])}
        <br />
        {renderContactMakeshipSupport()}
      </>
    );
  }

  const productCare = getProductCare(metafields)[productType] || {};

  // Make Good 2025
  const renderMakeGoodSpecsList = () => (
    <SpecsList>
      <SpecsListItem>
        <P2>Keychain: 11.5cm (H) x 10.5cm (W)</P2>
      </SpecsListItem>
      <SpecsListItem>
        <P2>Bag: 57cm (H) x 41cm (W)</P2>
      </SpecsListItem>
    </SpecsList>
  );

  const renderMakeGoodMaterials = () => (
    <SpecsList>
      <SpecsListItem>
        <P2>Keychain: PP Cotton, Plush Fabric, Nylex Fabric</P2>
      </SpecsListItem>
      <SpecsListItem>
        <P2>Bag: 100% polyester</P2>
      </SpecsListItem>
    </SpecsList>
  );

  const renderMakeGoodCareInstructions = () => (
    <SpecsList>
      <SpecsListItem>
        <P2>Keychain: Handwash warm or cool only. Do not machine wash or dry clean.</P2>
      </SpecsListItem>
      <SpecsListItem>
        <P2>Bag: Machine wash delicate OR hand wash and hang to dry.</P2>
      </SpecsListItem>
    </SpecsList>
  );

  return (
    <SpecsWrapper>
      {productCare.dimensions && (
        <SingleSpecWrapper>
          <FaintCaption>Dimensions</FaintCaption>
          {isMakeGoodProduct ? renderMakeGoodSpecsList() : renderSpecsList(productCare.dimensions)}
          {productCare.disclaimer && <FaintCaption>{productCare.disclaimer}</FaintCaption>}
        </SingleSpecWrapper>
      )}
      {productCare.weight && (
        <SingleSpecWrapper>
          <FaintCaption>Weight</FaintCaption>
          {renderSpecsList(productCare.weight)}
        </SingleSpecWrapper>
      )}
      {productCare.material && (
        <SingleSpecWrapper>
          <FaintCaption>Materials</FaintCaption>
          <SpecsList>
            {isMakeGoodProduct
              ? renderMakeGoodMaterials()
              : productCare.material.map((material) => (
                  <SpecsListItem>
                    <P2>{material}</P2>
                  </SpecsListItem>
                ))}
          </SpecsList>
        </SingleSpecWrapper>
      )}
      {productCare.careInstructions && (
        <SingleSpecWrapper>
          <FaintCaption>Care Instructions</FaintCaption>
          <P2>{isMakeGoodProduct ? renderMakeGoodCareInstructions() : productCare.careInstructions}</P2>
        </SingleSpecWrapper>
      )}
      {productType && (
        <SingleSpecWrapper>
          <P2>{getProductWarning(productType)}</P2>
        </SingleSpecWrapper>
      )}
      {isGorillaTagProduct && (
        <SingleSpecWrapper>
          <FaintCaption>Gorilla Tag Rewards (In-Game Code, Sticker + Trading Card)</FaintCaption>
          <P2>
            One of each reward per unit purchased. Physical Rewards will be shipped along with your product. In-game
            Reward Codes will be sent to your email by 3 PM EST on July 14th, 2025.
          </P2>
        </SingleSpecWrapper>
      )}
      {/* GITD 2024 */}
      {isHolidayHideawayProduct(product.id) && (
        <SingleSpecWrapper>
          <P2>
            <strong>Santa Fiya GITD Plushie not included in purchase of Holiday Hideaway GITD Plushie.</strong>
          </P2>
        </SingleSpecWrapper>
      )}
      {/* Make Good 2025 */}
      {isMakeGoodProduct && (
        <SingleSpecWrapper>
          <MakeGoodDisclaimer />
        </SingleSpecWrapper>
      )}
      {/* Pride 2025 */}
      {isPrideProduct && (
        <SingleSpecWrapper>
          <Pride2025Disclaimer />
        </SingleSpecWrapper>
      )}
      {renderContactMakeshipSupport()}
    </SpecsWrapper>
  );
};

export default SpecsToggle;
